import { MouseEvent, useEffect, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { isTouchEnabled } from '../../../../../Theme';
import useWidth from '../../../../../components/Hooks/useWidth';
import { useMediaApplicationContext } from '../../../../../components/MediaApplicationContext';
import teceoSlickerLib, {
  NavigationDirection,
} from '../../../../../components/Slicker/TeceoSlickerLib';
import useInternetInfo from '../../../../../hooks/useInternetInfo';
import useZoomGesture from '../../../../../hooks/useZoomGesture';
import useGeneralSettingsStore from '../../../../../stores/GeneralSettings/useGeneralSettingsStore';
import { Media, UseProductDetailZoomParams } from '../interfaces/productDetailZoom.interfaces';
import { getMediaAspectRatio } from '../utils/mediaAspectRatio.utils';

const NAVIGATION_DELAY = 100;

const useProductDetailZoom = <T extends Media>({
  medias,
  selectedMedia,
  focusPercentToScroll,
}: UseProductDetailZoomParams<T>) => {
  const getSelectedMediaIndex = () => {
    if (!selectedMedia) {
      return 0;
    }

    const foundIndex =
      medias?.findIndex(
        (media) => media.type === selectedMedia.type && media.url === selectedMedia.url,
      ) ?? -1;

    return Math.max(0, foundIndex);
  };

  const mediaSelectionKey = [
    medias?.length ?? 0,
    selectedMedia?.type ?? '',
    selectedMedia?.url ?? '',
    focusPercentToScroll,
  ].join('|');

  const [selectedIndex, setSelectedIndex] = useState(getSelectedMediaIndex);
  const [zoomAnchor, setZoomAnchor] = useState(focusPercentToScroll);
  const [isZoomApplied, setIsZoomApplied] = useState(false);
  const [previousMediaSelectionKey, setPreviousMediaSelectionKey] = useState(mediaSelectionKey);
  const navigationTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  if (previousMediaSelectionKey !== mediaSelectionKey) {
    setPreviousMediaSelectionKey(mediaSelectionKey);
    setSelectedIndex(getSelectedMediaIndex());
    setZoomAnchor(focusPercentToScroll);
  }

  const { mobileWidth } = useWidth();
  const { internetSpeed } = useInternetInfo();
  const mediaContext = useMediaApplicationContext();

  const { mediaFormat } = useGeneralSettingsStore(
    useShallow((state) => ({
      mediaFormat: state.generalSettings.mediaFormat,
    })),
  );

  useZoomGesture();

  useEffect(() => () => clearTimeout(navigationTimeout.current), []);

  const navigate = (direction: NavigationDirection) => {
    clearTimeout(navigationTimeout.current);

    const nextIndex = teceoSlickerLib.getNextIndex(selectedIndex, direction, medias?.length ?? 0);

    navigationTimeout.current = setTimeout(() => setSelectedIndex(nextIndex), NAVIGATION_DELAY);
  };

  const handleImageClick = (event: MouseEvent<HTMLImageElement>) => {
    const { top, height } = event.currentTarget.getBoundingClientRect();
    const clickedPercent = height > 0 ? (event.clientY - top) / height : 0;

    setIsZoomApplied((previousIsZoomApplied) => !previousIsZoomApplied);
    setZoomAnchor(Math.min(Math.max(clickedPercent, 0), 1));
  };

  const getMediaUrls = (url: string) => ({
    mediaUrl: mediaContext.getMediaFromSize(url, 'lg', 'webp'),
    previewUrl: mediaContext.getMediaFromSize(url, internetSpeed === 'fast' ? 'md' : 'sm', 'webp'),
  });

  const hasNavigation = (medias?.length ?? 0) > 1;

  return {
    navigate,
    zoomAnchor,
    mobileWidth,
    getMediaUrls,
    selectedIndex,
    isZoomApplied,
    handleImageClick,
    setSelectedIndex,
    hasNavigation,
    isSwipeable: isTouchEnabled && hasNavigation,
    isSwipeDisabled: isZoomApplied && !mobileWidth,
    mediaAspectRatio: getMediaAspectRatio(mediaFormat),
    currentMedia: medias?.[selectedIndex],
  };
};

export default useProductDetailZoom;
